import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthCheck } from '../../hooks/useAuthCheck';
import { useI18n } from '../../contexts/I18nContext';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import LoginModal from '../Login/LoginModal';
import './Navbar.scss';
import './Navbar.mobile.scss';
import { API_BASE_URL } from '../../lib/constants';
import toast from 'react-hot-toast';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, loading, isInitialized } = useAuth();
  const { checkAuthAndExecute, checkAuthAndRedirect, showLoginModal, handleCloseLogin, handleLoginSuccess } = useAuthCheck();
  const { t } = useI18n();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  const getText = (key) => {
    if (!key) return '';
    const keyMap = {
      'Home': 'navbar.home',
      'Ai Workbench': 'navbar.aiWorkbench',
      'Video Generation': 'catoo.videoGeneration',
      'MV Generation': 'navbar.mvGeneration',
      'My Creations': 'navbar.myCreations',
      'Pricing': 'navbar.pricing',
      'Showcase': 'navbar.showcase',
      'Logout': 'navbar.logout',
      'Sign In': 'navbar.signIn'
    };
    const translationKey = keyMap[key];
    return translationKey ? t(translationKey) : key;
  };

  // 处理导航链接点击，重置页面滚动位置
  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDrawerNavClick = () => {
    handleNavClick();
    setIsDrawerOpen(false);
  };

  const handleDrawerAuthNavClick = (e, path) => {
    handleAuthRequiredNavClick(e, path);
    setIsDrawerOpen(false);
  };

  const handleDrawerLogout = () => {
    handleLogout();
    setIsDrawerOpen(false);
  };

  // 改进的需要认证的导航点击处理 - 跳转到登录页面版本
  const handleAuthRequiredNavClick = (e, path) => {
    e.preventDefault();
    
    // 如果认证状态还在初始化中，等待初始化完成
    if (!isInitialized || loading) {
      return;
    }
    
    checkAuthAndRedirect(() => {
      // 使用navigate而不是window.location.href，避免页面刷新
      navigate(path);
      handleNavClick();
    });
  };

  const handleLogout = () => {
    logout();
    toast.success(t('login.loggedOutSuccessfully'));
    navigate('/', { replace: true });
    handleNavClick();
  };

  // 生成用户头像（如果没有头像则使用用户名首字母）
  const renderUserAvatar = () => {
    if (user?.avatar) {
      const avatarUrl = user.avatar.startsWith('http') ? user.avatar : `${API_BASE_URL}${user.avatar}`;
      return (
        <img 
          src={avatarUrl} 
          alt='Unlike raw API calls, our optimized Gemini interface provides a zero-config experience for creators.' 
          className="user-avatar"
        />
      );
    } else {
      const firstChar = (user?.name || user?.email || 'U').charAt(0).toUpperCase();
      return (
        <div className="user-avatar-char">
          {firstChar}
        </div>
      );
    }
  };

  return (
    <header className="site-header">
      <div className="nav">
        <div className="brand">
          <Link to="/" onClick={handleNavClick} style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            <img src="/logo.png" alt="Unlike raw API calls, our optimized Gemini interface provides a zero-config experience for creators." style={{ width: 48, height: 48 }} />
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', margin: '0 0 0 5px' }}>
              <span>Catoo</span>
              <span style={{ fontSize: '15px' }}>Built with Gemini</span>
            </div>
          </Link>
        </div>
        <button className="mobile-menu-toggle" onClick={() => setIsDrawerOpen(true)}>
          ☰
        </button>
        <nav className="main-nav">
          <Link 
            to="/" 
            className={location.pathname === '/' ? 'active' : ''}
            onClick={handleNavClick}
          >
            {getText('Home')}
          </Link>
          <Link 
            to="/ai-tools-workbench" 
            className={location.pathname === '/ai-tools-workbench' ? 'active' : ''}
            onClick={handleNavClick}
          >
            {getText('Ai Workbench')}
          </Link>
          <Link 
            to="/ai-tools/veo-video-generator" 
            className={location.pathname === '/ai-tools/veo-video-generator' ? 'active' : ''}
            onClick={handleNavClick}
          >
            {getText('Video Generation')}
          </Link>
          <Link
            to="/ai-tools/mv-generator"
            className={location.pathname === '/ai-tools/mv-generator' ? 'active' : ''}
            onClick={handleNavClick}
          >
            {getText('MV Generation')}
          </Link>
          <Link 
            to="/creation-record" 
            className={location.pathname === '/creation-record' ? 'active' : ''}
            onClick={(e) => handleAuthRequiredNavClick(e, '/creation-record')}
          >
            {getText('My Creations')}
          </Link>
          <Link 
            to="/pricing" 
            className={location.pathname === '/pricing' ? 'active' : ''}
            onClick={handleNavClick}
          >
            {getText('Pricing')}
          </Link>
          <Link 
            to="/showcase" 
            className={location.pathname === '/showcase' ? 'active' : ''}
            onClick={handleNavClick}
          >
            {getText('Showcase')}
          </Link>
        </nav>
        <div className="actions-group">
          <LanguageSelector />
          {isAuthenticated ? (
            <div className="user-menu">
              <Link to="/personal-center" onClick={handleNavClick} className="user-profile-link">
                {renderUserAvatar()}
                <span className="user-name">{user?.name || user?.email}</span>
              </Link>
              <div className="user-dropdown">
                <Link to="/personal-center" onClick={handleNavClick} className="dropdown-item">
                  {t('navbar.profile') || 'Profile'}
                </Link>
                <div className="dropdown-divider" />
                <button className="dropdown-item is-danger" onClick={handleLogout}>
                  {getText('Logout')}
                </button>
              </div>
            </div>
          ) : (
            <button className="btn btn-primary" onClick={() => checkAuthAndExecute(() => {})}>
              {getText('Sign In')}
            </button>
          )}
        </div>
      </div>
      <div className={`drawer-overlay ${isDrawerOpen ? 'open' : ''}`} onClick={() => setIsDrawerOpen(false)} />
      <aside className={`mobile-drawer ${isDrawerOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <div className="brand">
            <Link to="/" onClick={handleDrawerNavClick} style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
              <img src="/logo.png" alt="Unlike raw API calls, our optimized Gemini interface provides a zero-config experience for creators." style={{ width: 32, height: 32 }} />
              <span>Catoo</span>
            </Link>
          </div>
          <button className="drawer-close" onClick={() => setIsDrawerOpen(false)}>
            ✕
          </button>
        </div>
        <div className="drawer-content">
          <Link to="/" className={location.pathname === '/' ? 'active' : ''} onClick={handleDrawerNavClick}>
            {getText('Home')}
          </Link>
          <Link
            to="/ai-tools-workbench"
            className={location.pathname === '/ai-tools-workbench' ? 'active' : ''}
            onClick={handleDrawerNavClick}
          >
            {getText('Ai Workbench')}
          </Link>
          <Link
            to="/ai-tools/veo-video-generator"
            className={location.pathname === '/ai-tools/veo-video-generator' ? 'active' : ''}
            onClick={handleDrawerNavClick}
          >
            {getText('Video Generation')}
          </Link>
          <Link
            to="/ai-tools/mv-generator"
            className={location.pathname === '/ai-tools/mv-generator' ? 'active' : ''}
            onClick={handleDrawerNavClick}
          >
            {getText('MV Generation')}
          </Link>
          <Link
            to="/creation-record"
            className={location.pathname === '/creation-record' ? 'active' : ''}
            onClick={(e) => handleDrawerAuthNavClick(e, '/creation-record')}
          >
            {getText('My Creations')}
          </Link>
          <Link
            to="/pricing"
            className={location.pathname === '/pricing' ? 'active' : ''}
            onClick={handleDrawerNavClick}
          >
            {getText('Pricing')}
          </Link>
          <Link
            to="/showcase"
            className={location.pathname === '/showcase' ? 'active' : ''}
            onClick={handleDrawerNavClick}
          >
            {getText('Showcase')}
          </Link>
          {isAuthenticated && (
            <Link to="/personal-center" onClick={handleDrawerNavClick} className="drawer-user">
              {renderUserAvatar()}
              <span>{user?.name || user?.email}</span>
            </Link>
          )}
          {isAuthenticated ? (
            <button className="dropdown-item is-danger" onClick={handleDrawerLogout}>
              {getText('Logout')}
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => { checkAuthAndExecute(() => {}); setIsDrawerOpen(false); }}>
              {getText('Sign In')}
            </button>
          )}
        </div>
        <div className="drawer-footer">
          <LanguageSelector />
        </div>
      </aside>
      
      {/* 登录弹窗 */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={handleCloseLogin}
        onLoginSuccess={handleLoginSuccess}
      />
    </header>
  );
};

export default Navbar;
